// src/components/tickets/TicketItem.js
import React, { useState, useEffect } from 'react';
import QRGenerator from './QRGenerator';
import Gamification from '../gamification/Gamification';
import AddToWalletButton from './AddToWalletButton';
import ListOnMarketplace from '../marketplace/ListOnMarketplace';
import DevScannerSimulator from './DevScannerSimulator';
import { getPerkInfo } from '../../utils/blockchain';
import { resolveIPFS, fetchMetadata } from '../../services/ipfs';

export default function TicketItem({ ticket, eventTicket, account }) {
  const [showDetails, setShowDetails] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ticketState, setTicketState] = useState(null);
  const [showMarketplaceModal, setShowMarketplaceModal] = useState(false);

  // Load ticket state (used, claimedPerks) on expand
  useEffect(() => {
    const loadTicketState = async () => {
      if (!eventTicket || !showDetails) return;
      try {
        const state = await eventTicket.tickets(ticket.ticketTypeId, ticket.serialNumber);
        setTicketState({
          used: Boolean(state.used),
          claimedPerks: Array.isArray(state.claimedPerks)
            ? state.claimedPerks.map(p => Number(p))
            : [],
          checkinTime: Number(state.checkinTime || 0),
        });
      } catch (err) {
        console.error('Failed to load ticket state:', err);
        setTicketState({
          used: ticket.used || false,
          claimedPerks: [],
          checkinTime: 0,
        });
      }
    };

    loadTicketState();
  }, [eventTicket, ticket.ticketTypeId, ticket.serialNumber, ticket.used, showDetails]);

  // Load metadata from URI on expand
  const loadMetadata = async () => {
    if (metadata || !ticket.metadataURI) return;

    setLoading(true);
    try {
      let url = ticket.metadataURI;

      // Support {id} template
      if (url.includes('{id}')) {
        url = url.replace('{id}', ticket.ticketTypeId);
      }

      // Fetch if it looks like a URL/IPFS URI
      if (url.startsWith('http') || url.includes('ipfs') || url.startsWith('ipfs://')) {
        const data = await fetchMetadata(url);
        setMetadata(data);
      } else {
        setMetadata({ name: url });
      }
    } catch (error) {
      console.error('Failed to load metadata from:', ticket.metadataURI, error);
      setMetadata({ name: 'Unnamed Ticket' });
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (!showDetails && !metadata) {
      loadMetadata();
    }
    setShowDetails(!showDetails);
  };

  const getStatusBadge = () => {
    const isUsed = ticketState?.used ?? ticket.used;
    if (isUsed) {
      return <span className="badge used">Used</span>;
    }
    return <span className="badge active">Active</span>;
  };

  // Extract event info from metadata attributes
  const getEventInfoFromMetadata = () => {
    if (!metadata?.attributes) return null;

    let eventDate = null;
    let venue = null;

    for (const attr of metadata.attributes) {
      if (attr.trait_type === 'Event Date' && attr.value) {
        eventDate = new Date(attr.value);
      }
      if (attr.trait_type === 'Venue' && attr.value) {
        venue = attr.value;
      }
    }

    return { eventDate, venue };
  };

  const eventInfo = getEventInfoFromMetadata();

  // Get the best available name for the ticket
  const getTicketDisplayName = () => {
    // Priority 1: On-chain ticket type name + event name
    if (ticket.name && ticket.eventName) {
      return `${ticket.eventName} - ${ticket.name}`;
    }
    // Priority 2: On-chain ticket type name only
    if (ticket.name) {
      return ticket.name;
    }
    // Priority 3: Metadata name
    if (metadata?.name && metadata.name !== 'Unnamed Ticket' && metadata.name !== 'Ticket') {
      return metadata.name;
    }
    // Priority 4: Fallback with ticket info
    return `Ticket #${ticket.serialNumber} (Type ${ticket.ticketTypeId})`;
  };

  // Get the best available image
  const getTicketImage = () => {
    if (metadata?.image) {
      return resolveIPFS(metadata.image);
    }
    return null;
  };

  return (
    <div className="ticket-item">
      <div className="ticket-summary" onClick={handleClick}>
        <div className="ticket-info">
          <h4>{getTicketDisplayName()}</h4>
          {ticket.eventName && (
            <p className="ticket-event-name">{ticket.eventName}</p>
          )}
          {ticket.name && (
            <p className="ticket-type-name">{ticket.name}</p>
          )}
          {!ticket.name && !ticket.eventName && (
            <p>Type: {ticket.ticketTypeId}</p>
          )}
          {eventInfo?.eventDate && (
            <p className="ticket-date">Date: {eventInfo.eventDate.toLocaleDateString()}</p>
          )}
        </div>
        <div className="ticket-status">
          {getStatusBadge()}
          <span className="arrow">{showDetails ? '\u25B2' : '\u25BC'}</span>
        </div>
      </div>

      {showDetails && (
        <div className="ticket-details">
          {loading ? (
            <div className="loading">Loading ticket details...</div>
          ) : (
            <>
              {/* Show ticket/event image */}
              {getTicketImage() && (
                <img
                  src={getTicketImage()}
                  alt={ticket.name || metadata?.name || 'Ticket'}
                  className="ticket-image"
                />
              )}

              {/* Show description from metadata */}
              {metadata?.description && (
                <p className="ticket-description">{metadata.description}</p>
              )}

              {/* Event Info */}
              {(eventInfo?.eventDate || eventInfo?.venue) && (
                <div className="event-info">
                  <h5>Event Details</h5>
                  {eventInfo.eventDate && (
                    <p><strong>Date:</strong> {eventInfo.eventDate.toLocaleString()}</p>
                  )}
                  {eventInfo.venue && (
                    <p><strong>Venue:</strong> {eventInfo.venue}</p>
                  )}
                </div>
              )}

              {/* Perks */}
              {ticket.perks && ticket.perks.length > 0 && (
                <div className="perks-section">
                  <h5>Perks Included:</h5>
                  <ul>
                    {ticket.perks.map(perkId => {
                      const perk = getPerkInfo(perkId);
                      const isClaimed = ticketState?.claimedPerks?.includes(perkId);
                      return (
                        <li key={perkId}>
                          <strong>{perk.name}</strong>: {perk.description}
                          {isClaimed && <span className="claimed"> Claimed</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="ticket-actions">
                <h5>Actions</h5>
                <QRGenerator ticket={ticket} action="checkin" ticketState={ticketState} holderAddress={account} />
                <AddToWalletButton
                  contractAddress={eventTicket?.target || eventTicket?.address}
                  tokenId={ticket.tokenId}
                  imageUrl={getTicketImage()}
                  hideWhenAdded={true}
                />
                <button
                  className="list-marketplace-btn"
                  onClick={() => setShowMarketplaceModal(true)}
                >
                  List on Marketplace
                </button>
              </div>

              {/* Gamification - Real XP System */}
              <Gamification ticket={ticket} account={account} />

              {/* Dev Scanner Simulator - only shows in development */}
              <DevScannerSimulator
                ticket={ticket}
                account={account}
                eventTicket={eventTicket}
                onCheckinComplete={(result) => {
                  console.log('[TicketItem] Check-in complete:', result);
                }}
              />
            </>
          )}
        </div>
      )}

      {/* Marketplace Modal */}
      {showMarketplaceModal && (
        <ListOnMarketplace
          ticket={ticket}
          account={account}
          onClose={() => setShowMarketplaceModal(false)}
        />
      )}
    </div>
  );
}
